/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DataTable from "components/DataTable";
import OnnaDeviceForm from "layouts/project/components/onnaDeviceForm";
import { useSelector } from "react-redux";

function CellDate({ value }) {
  return <span>{value ? moment(value).format("DD/MM/YYYY HH:mm") : ""}</span>;
}

function CellState({ value }) {
  if (value) {
    return <Icon color="success">circle</Icon>;
  }
  return <Icon color="error">circle</Icon>;
}

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function OnnaDevices({ database, onnaDevices, projectId }) {
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");

  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = () => [
    { Header: t("state"), accessor: "connected", Cell: CellState, width: 10 },
    { Header: t("name"), accessor: "name", width: 20 },
    { Header: t("onnaId"), accessor: "onnaId", width: 20 },
    {
      Header: t("createdAt"),
      accessor: "createdAt",
      Cell: CellDate,
      maxWidth: 100,
    },
  ];

  const remove = (data) => {
    const dataToSend = [];
    data.forEach((onnaDevice) => {
      const { original } = onnaDevice;
      dataToSend.push(original);
    });
    setActionType("remove");
    setUids(dataToSend);
    setOpenConfirm(true);
  };

  const onSubmit = (data) => {
    switch (actionType) {
      case "edit":
        database.editOnnaDevice(data);
        setOpen(false);
        break;
      case "add":
        database.addOnnaDevice(data, projectId);
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const confirm = () => {
    switch (actionType) {
      case "remove":
        database.removeOnnaDevices(uids, projectId);
        setActionType("");
        setOpenConfirm(false);
        break;
      default:
        break;
    }
  };

  const add = () => {
    setActionType("add");
    setOpen(true);
  };

  const edit = (indexes) => {
    const onnaIndexes = [];
    indexes.forEach((index) => {
      const {
        original: { onnaId },
      } = index;
      onnaIndexes.push(onnaId);
    });
    setUids(onnaIndexes);
    setActionType("edit");
    setOpen(true);
  };

  const menuConfig = useSelector((state) => state.mainUserReducer.customApp.menuConfig);
  const onnaDevicesLabel = t(
    menuConfig?.onnaDevices?.label ?? menuConfig?.onnaDevices?.defaultLabel ?? "onnaDevices"
  );

  return (
    <Card>
      <AlertDialog
        open={openConfirm}
        handleClose={() => {
          setOpenConfirm(false);
        }}
        handleAgree={() => {
          confirm();
        }}
      />
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <OnnaDeviceForm onSubmit={onSubmit} actionType={actionType} uids={uids} />
        </MDBox>
      </Modal>
      <CardTitle title={onnaDevicesLabel} icon="hub" />
      <DataTable
        table={{ columns: columns(), rows: Object.values(onnaDevices || {}) }}
        canSearch
        canSelect
        canEdit
        canAdd
        canActuate
        action={(data) => {
          const onnaId = data?.original?.onnaId;
          if (onnaId) {
            navigate(`/projects/${projectId}/onnaDevice/${onnaId}`);
          }
        }}
        actionIcon="arrow_forward_ios"
        actionColor="info"
        add={add}
        edit={edit}
        remove={remove}
      />
    </Card>
  );
}

CellState.defaultProps = {
  value: false,
};

CellState.propTypes = {
  value: PropTypes.bool,
};

CellDate.defaultProps = {
  value: 0,
};

CellDate.propTypes = {
  value: PropTypes.number,
};

OnnaDevices.defaultProps = {
  onnaDevices: {},
  projectId: "",
};

OnnaDevices.propTypes = {
  onnaDevices: PropTypes.object,
  projectId: PropTypes.string,
  database: PropTypes.object.isRequired,
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};

export default OnnaDevices;
